import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Eye, EyeOff, Star, GripVertical } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { AdminNav } from './AdminDashboard';
import { useAuth } from '../../lib/auth';
import { getImageUrl } from '../../lib/utils-img';
import API from '../../lib/api';
import { toast } from 'sonner';

export default function AdminLocations() {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (authLoading) return; if (!isAdmin) { navigate('/login'); return; } load(); }, [isAdmin, authLoading, navigate]);
  const load = () => { API.get('/admin/locations').then(r => { setLocations(r.data.locations); setLoading(false); }).catch(() => setLoading(false)); };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try { await API.delete(`/admin/locations/${id}`); toast.success('Location deleted'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const toggleVisibility = async (loc) => {
    try { await API.put(`/admin/locations/${loc.id}`, { is_visible: !loc.is_visible }); toast.success(`Location ${loc.is_visible ? 'hidden' : 'visible'}`); load(); }
    catch { toast.error('Update failed'); }
  };

  const toggleFeatured = async (loc) => {
    try { await API.put(`/admin/locations/${loc.id}`, { is_featured: !loc.is_featured }); toast.success(`Location ${loc.is_featured ? 'unfeatured' : 'featured'}`); load(); }
    catch { toast.error('Update failed'); }
  };

  return (
    <div className="min-h-screen pt-20 bg-[#101010]" data-testid="admin-locations">
      <div className="bg-[#161616] border-b border-white/[0.06] py-8 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div><p className="text-gold text-xs uppercase tracking-[0.3em] font-semibold">Admin</p><h1 className="font-heading text-2xl text-white font-bold mt-1">Locations</h1></div>
          <Link to="/admin/locations/new">
            <Button className="bg-gold hover:bg-[#9A7B1F] text-white text-xs uppercase tracking-widest rounded-lg gap-1.5" data-testid="new-location-btn"><Plus className="w-3.5 h-3.5" /> New Location</Button>
          </Link>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <AdminNav active="/admin/locations" />
        {loading ? <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-white/[0.03] rounded-xl animate-pulse" />)}</div> : locations.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-white/35 mb-4">No locations yet. Create your first one.</p>
            <Link to="/admin/locations/new"><Button className="bg-gold hover:bg-[#9A7B1F] text-white rounded-lg text-xs uppercase tracking-widest gap-1.5"><Plus className="w-3.5 h-3.5" /> Create Location</Button></Link>
          </div>
        ) : (
          <div className="space-y-3">
            {locations.map((loc, i) => (
              <motion.div key={loc.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-4 hover:border-white/[0.12] transition-all" data-testid={`location-row-${loc.id}`}>
                <div className="flex items-center gap-3">
                  <div className="hidden sm:block text-white/15 cursor-grab"><GripVertical className="w-4 h-4" /></div>
                  <div className="w-12 h-10 sm:w-16 sm:h-12 rounded-lg bg-white/[0.06] overflow-hidden shrink-0">
                    {loc.thumbnail_image && <img src={getImageUrl(loc.thumbnail_image)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-white truncate">{loc.name}</h3>
                      {loc.is_featured && <span className="text-[10px] bg-gold/15 text-gold px-1.5 py-0.5 rounded font-medium">Featured</span>}
                      {!loc.is_visible && <span className="text-[10px] bg-white/[0.05] text-white/30 px-1.5 py-0.5 rounded font-medium">Hidden</span>}
                    </div>
                    <p className="text-xs text-white/35 truncate">{loc.short_description} &middot; {loc.listing_count} listings</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-3 sm:mt-0 border-t border-white/[0.04] sm:border-0 pt-3 sm:pt-0">
                  <button onClick={() => toggleFeatured(loc)} className={`p-2 rounded-lg transition-colors ${loc.is_featured ? 'text-gold hover:bg-gold/10' : 'text-white/20 hover:text-white/40 hover:bg-white/[0.05]'}`} title="Toggle featured"><Star className="w-4 h-4" fill={loc.is_featured ? 'currentColor' : 'none'} /></button>
                  <button onClick={() => toggleVisibility(loc)} className={`p-2 rounded-lg transition-colors ${loc.is_visible ? 'text-emerald-400 hover:bg-emerald-500/10' : 'text-white/20 hover:bg-white/[0.05]'}`} title="Toggle visibility">{loc.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
                  <Link to={`/admin/locations/${loc.id}/edit`} className="p-2 rounded-lg text-white/30 hover:text-white hover:bg-white/[0.05] transition-colors" data-testid={`edit-location-${loc.id}`}><Edit className="w-4 h-4" /></Link>
                  <button onClick={() => handleDelete(loc.id, loc.name)} className="p-2 rounded-lg text-white/20 hover:text-red-400 hover:bg-red-500/10 transition-colors" data-testid={`delete-location-${loc.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
